import { useEffect, useMemo, useState } from 'react';
import { Box, Button, Divider, Stack, Typography } from '@mui/material';
import { useTranslations } from '@/i18n/use-translations';
import type { GetEventSettingsDto } from '@/types/dtos/events/response/get-event-settings.dto';
import type { EventSessionTypeCatalogEntryDto, EventSessionTypePolicyDto } from '@/types/dtos/media/media-settings.dto';
import MediaSettingsSwitchRow from '@/components/media/media-settings-switch-row';
import {
  type MediaSettingsConfirmKey,
  mediaSettingsConfirmMessageKey,
} from '@/components/media/media-settings-confirm.types';
import { usePatchEventSessionTypesSettingsMutation } from '@/state/mutations/events/use-patch-event-session-types-settings';
import { useNotifications } from '@/hooks/use-notifications';
import { getObjectId } from '@/helpers/object.id.helper';
import { useEventStore } from '@/state/stores/use-event-store';
import EventCatalogProductsListModal from '@/components/events/event-catalog/event-catalog-products-list.modal';
import SessionTypeInfo from '@/components/common/session-type-info';
import { useGetCompositeComponentsQuery } from '@/state/queries/composite-components/use-get-composite-components';
import { compositeMatchesSessionTypePolicy } from '@/helpers/composite-matches-session-type.helper';
import type { SessionType } from '@/types/dtos/sessions/session-type.enum';

type PolicyConfirmField = 'offered' | 'allowSelfCreate' | 'allowReorder';

type PendingChange = {
  type: SessionType;
  field: PolicyConfirmField;
  patch: Partial<EventSessionTypePolicyDto>;
  confirmKey: MediaSettingsConfirmKey;
};

const POLICY_FIELD_CONFIRM: Record<PolicyConfirmField, MediaSettingsConfirmKey> = {
  offered: 'sessionTypes.offered.off',
  allowSelfCreate: 'sessionTypes.allowSelfCreate.off',
  allowReorder: 'sessionTypes.allowReorder.off',
};

export default function EventSessionTypesSettingsForm({
  eventId,
  initialSettings,
}: {
  eventId: string;
  initialSettings?: GetEventSettingsDto;
}) {
  const t = useTranslations();
  const notification = useNotifications();
  const event = useEventStore((s) => s.event);
  const originEventId = event?.origin ? getObjectId(event.origin) : eventId;

  const catalog = initialSettings?.sessionTypes?.catalog ?? [];
  const canEdit = initialSettings?.mediaSettingsCapabilities?.canEdit ?? false;

  const [localByType, setLocalByType] = useState<Record<string, EventSessionTypePolicyDto>>({});
  const [pending, setPending] = useState<PendingChange | null>(null);
  const [upgradeKeys, setUpgradeKeys] = useState<string[] | null>(null);

  useEffect(() => {
    if (initialSettings?.sessionTypes?.byType) {
      setLocalByType(initialSettings.sessionTypes.byType);
    }
  }, [initialSettings?.sessionTypes?.byType]);

  const rows: EventSessionTypeCatalogEntryDto[] = useMemo(() => {
    if (catalog.length) {
      return catalog;
    }
    return Object.entries(initialSettings?.sessionTypes?.byType ?? {}).map(([type, policy]) => ({
      type,
      isEntitled: true,
      stored: policy,
      effective: policy,
    }));
  }, [catalog, initialSettings?.sessionTypes?.byType]);

  const { mutateAsync, isPending } = usePatchEventSessionTypesSettingsMutation();
  const { data: composites = [] } = useGetCompositeComponentsQuery();

  const getApplicableForSessionType = (type: string) =>
    composites.filter((c) => compositeMatchesSessionTypePolicy(c, type)).map((c) => ({ slug: c.slug, name: c.name }));

  const toggleVirtualSlug = async (type: SessionType, slug: string, enabled: boolean) => {
    const current = localByType[type]?.virtualMedia?.enabledSlugs ?? [];
    const next = enabled ? [...new Set([...current, slug])] : current.filter((s) => s !== slug);
    await applyPatch(type, { virtualMedia: { enabledSlugs: next } });
  };

  const applyPatch = async (type: SessionType, patch: Partial<EventSessionTypePolicyDto>) => {
    try {
      await mutateAsync({ eventId: originEventId, byType: { [type]: patch } });
      setLocalByType((prev) => {
        const current = prev[type];
        const nextVirtual =
          patch.virtualMedia !== undefined
            ? {
                ...(current?.virtualMedia ?? { enabledSlugs: [] }),
                ...patch.virtualMedia,
              }
            : current?.virtualMedia;
        return {
          ...prev,
          [type]: {
            ...current,
            ...patch,
            ...(nextVirtual !== undefined ? { virtualMedia: nextVirtual } : {}),
          } as EventSessionTypePolicyDto,
        };
      });
      notification.notifySuccess(t('media.settings.sessionTypesSaveSuccess'));
    } catch {
      notification.notifyError(t('media.settings.sessionTypesSaveError'));
    }
  };

  const cancelPending = () => {
    if (pending) {
      setLocalByType((prev) => {
        const current = prev[pending.type];
        if (!current) return prev;
        return {
          ...prev,
          [pending.type]: {
            ...current,
            [pending.field]: !current[pending.field],
          },
        };
      });
    }
    setPending(null);
  };

  const confirmPending = () => {
    const change = pending;
    setPending(null);
    if (change) {
      void applyPatch(change.type, change.patch);
    }
  };

  const switchConfirm = (type: string, field: PolicyConfirmField) => {
    if (pending?.type !== type || pending.field !== field) return null;
    return {
      message: t(mediaSettingsConfirmMessageKey(pending.confirmKey)),
      onCancel: cancelPending,
      onConfirm: confirmPending,
    };
  };

  const requestPolicyChange = (row: EventSessionTypeCatalogEntryDto, field: PolicyConfirmField, value: boolean) => {
    if (!row.isEntitled) {
      return;
    }
    const patch = { [field]: value };
    if (!value) {
      setLocalByType((prev) => ({
        ...prev,
        [row.type]: {
          ...(prev[row.type] ?? row.stored),
          [field]: value,
        },
      }));
      setPending({
        type: row.type as SessionType,
        field,
        patch,
        confirmKey: POLICY_FIELD_CONFIRM[field],
      });
      return;
    }
    void applyPatch(row.type as SessionType, patch);
  };

  const getPolicy = (row: EventSessionTypeCatalogEntryDto): EventSessionTypePolicyDto => {
    return localByType[row.type] ?? row.stored;
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pr: 1 }}>
      <Typography variant="h6">{t('event.detail.settingsMenu.sessionTypes.title')}</Typography>
      <Typography variant="body2" color="text.secondary">
        {t('event.detail.settingsMenu.sessionTypes.description')}
      </Typography>
      <Stack spacing={2} divider={<Divider flexItem />}>
        {rows.map((row) => {
          const policy = getPolicy(row);
          const rowDisabled = !canEdit || !row.isEntitled || isPending;
          const keys = row.requiredCommercialFeatureKeys?.join(', ') ?? '';

          return (
            <Box key={row.type}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                <SessionTypeInfo type={row.type as SessionType} />
                {!row.isEntitled && row.requiredCommercialFeatureKeys?.length ? (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => setUpgradeKeys(row.requiredCommercialFeatureKeys ?? [])}
                  >
                    {t('media.settings.sessionTypes.upgrade')}
                  </Button>
                ) : null}
              </Stack>
              <MediaSettingsSwitchRow
                switchKey={`${row.type}-offered-${policy.offered}`}
                label={t('media.settings.sessionTypes.offered')}
                checked={policy.offered}
                disabled={rowDisabled}
                tooltip={!row.isEntitled ? t('media.settings.sessionTypes.disabledNotEntitled') : undefined}
                confirm={switchConfirm(row.type, 'offered')}
                onChange={(v) => requestPolicyChange(row, 'offered', v)}
              />
              <MediaSettingsSwitchRow
                switchKey={`${row.type}-allowSelfCreate-${policy.allowSelfCreate}`}
                label={t('media.settings.sessionTypes.allowSelfCreate')}
                checked={policy.allowSelfCreate}
                disabled={rowDisabled}
                tooltip={
                  !row.isEntitled
                    ? keys
                      ? t('media.settings.sessionTypes.upgradeTooltip', { keys })
                      : t('media.settings.sessionTypes.disabledNotEntitled')
                    : undefined
                }
                confirm={switchConfirm(row.type, 'allowSelfCreate')}
                onChange={(v) => requestPolicyChange(row, 'allowSelfCreate', v)}
              />
              <MediaSettingsSwitchRow
                switchKey={`${row.type}-allowReorder-${policy.allowReorder}`}
                label={t('media.settings.sessionTypes.allowReorder')}
                checked={policy.allowReorder}
                disabled={rowDisabled}
                tooltip={!row.isEntitled ? t('media.settings.sessionTypes.disabledNotEntitled') : undefined}
                confirm={switchConfirm(row.type, 'allowReorder')}
                onChange={(v) => requestPolicyChange(row, 'allowReorder', v)}
              />
              {getApplicableForSessionType(row.type).length > 0 ? (
                <Box sx={{ mt: 1.5 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    {t('media.virtualSettings.sessionTypes.title')}
                  </Typography>
                  {getApplicableForSessionType(row.type).map((composite) => {
                    const enabled = (policy.virtualMedia?.enabledSlugs ?? []).includes(composite.slug);
                    return (
                      <MediaSettingsSwitchRow
                        key={`${row.type}-virtual-${composite.slug}`}
                        switchKey={`${row.type}-virtual-${composite.slug}-${enabled}`}
                        label={composite.name}
                        checked={enabled}
                        disabled={rowDisabled}
                        onChange={(v) => void toggleVirtualSlug(row.type as SessionType, composite.slug, v)}
                      />
                    );
                  })}
                </Box>
              ) : null}
            </Box>
          );
        })}
      </Stack>

      <EventCatalogProductsListModal
        open={upgradeKeys !== null}
        onModalClose={() => setUpgradeKeys(null)}
        event={event ?? undefined}
        commercialFeatureKeysFilter={upgradeKeys ?? undefined}
      />
    </Box>
  );
}
